"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AuthenticationResponseJSONV2 = exports.AuthenticationResponseJSON = exports.PublicKeyCredentialRequestOptions = exports.RegistrationResponseJSON = exports.AuthenticatorCreatedFromClient = exports.AuthenticatorCreatedFromPlatform = exports.CredentialCreationOptions = void 0;
const t = __importStar(require("io-ts"));
/**
 * This file contains the Codec used to verify the payloads exchanged between the client and the server during the
 * registration of a new passkey and the authentication of an existing one.
 *
 * Registration:
 * - The client asks the server for the registration options {@link CredentialCreationOptions}
 * - The server sends the registration options to the client {@link CredentialCreationOptions}
 * - The client signs the registration options and sends the result to the server {@link RegistrationResponseJSON}
 * - The server validate the registration options and register the passkey
 *
 * Authentication:
 * - The client asks the server for the authentication options {@link PublicKeyCredentialRequestOptions}
 * - The server sends the authentication options to the client {@link PublicKeyCredentialRequestOptions}
 * - The client signs the authentication options and sends the result to the server {@link AuthenticationResponseJSON}
 * - The server validate the authentication options and authenticate the passkey, returning a new valid session
 *
 * All the others types in this file are used to compose the previous ones.
 */
/**
 * https://w3c.github.io/webauthn/#enum-transport
 */
const AuthenticatorTransportFuture = t.union([
    t.literal("ble"),
    t.literal("internal"),
    t.literal("nfc"),
    t.literal("usb"),
    t.literal("cable"),
    t.literal("smart-card"),
    t.literal("hybrid")
]);
const AuthenticatorAttachment = t.union([
    t.literal("cross-platform"),
    t.literal("platform")
]);
const ResidentKeyRequirement = t.union([
    t.literal("discouraged"),
    t.literal("preferred"),
    t.literal("required")
]);
const UserVerificationRequirement = t.union([
    t.literal("discouraged"),
    t.literal("preferred"),
    t.literal("required")
]);
const AttestationConveyancePreference = t.union([
    t.literal("direct"),
    t.literal("enterprise"),
    t.literal("indirect"),
    t.literal("none")
]);
const PublicKeyCredentialEntity = t.type({
    name: t.string
});
const PublicKeyCredentialRpEntity = t.intersection([
    PublicKeyCredentialEntity,
    t.type({
        id: t.string
    })
]);
const PublicKeyCredentialUserEntity = t.intersection([
    PublicKeyCredentialEntity,
    t.type({
        id: t.string,
        displayName: t.string
    })
]);
const PublicKeyCredentialParameters = t.type({
    type: t.literal("public-key"),
    alg: t.number
});
const PublicKeyCredentialDescriptor = t.intersection([
    t.type({
        type: t.literal("public-key"),
        id: t.string
    }),
    t.partial({
        transports: t.array(AuthenticatorTransportFuture)
    })
]);
const AuthenticatorSelectionCriteria = t.partial({
    authenticatorAttachment: AuthenticatorAttachment,
    requireResidentKey: t.boolean,
    residentKey: ResidentKeyRequirement,
    userVerification: UserVerificationRequirement
});
/**
 * Registration Challenge request:
 * The server send to the client the challenge to register a new passkey
 * see https://w3c.github.io/webauthn/#dictionary-makecredentialoptions
 */
exports.CredentialCreationOptions = t.intersection([
    t.type({
        rp: PublicKeyCredentialRpEntity,
        user: PublicKeyCredentialUserEntity,
        challenge: t.string,
        pubKeyCredParams: t.array(PublicKeyCredentialParameters),
        // should be optional, changed for library compatibility
        timeout: t.number,
        // should be optional, changed for library compatibility
        attestation: AttestationConveyancePreference,
        // should be optional, changed for library compatibility
        authenticatorSelection: AuthenticatorSelectionCriteria
    }),
    t.partial({
        excludeCredentials: t.array(PublicKeyCredentialDescriptor),
        attestationFormats: t.array(t.string),
        extensions: t.record(t.string, t.unknown)
    })
]);
const AuthenticatorAttestationResponseJSON = t.intersection([
    t.type({
        clientDataJSON: t.string,
        attestationObject: t.string
    }),
    t.partial({
        transports: t.array(AuthenticatorTransportFuture)
    })
]);
const AuthenticationExtensionsClientOutputs = t.partial({
    appid: t.boolean,
    credProps: t.partial({
        rk: t.boolean
    }),
    hmacCreateSecret: t.boolean
});
exports.AuthenticatorCreatedFromPlatform = t.keyof({
    ios: null,
    android: null,
    windows: null,
    mac: null,
    linux: null,
    other: null
});
exports.AuthenticatorCreatedFromClient = t.keyof({
    web: null,
    mobile: null,
    other: null
});
/*
 * Registration Verification:
 * The FIDO2 Attestation Result, see https://w3c.github.io/webauthn/#dictdef-registrationresponsejson
 * In addition to the standard, we add the createdFrom and createdPlatform fields
 * to track where the registration was created from.
 * The response that the client sends to the server after signing in the previous received challenge,
 * to complete registration of a new passkey
 */
exports.RegistrationResponseJSON = t.intersection([
    t.type({
        id: t.string,
        rawId: t.string,
        response: AuthenticatorAttestationResponseJSON,
        clientExtensionResults: AuthenticationExtensionsClientOutputs,
        type: t.literal("public-key"),
        createdFrom: exports.AuthenticatorCreatedFromClient,
        createdPlatform: exports.AuthenticatorCreatedFromPlatform
    }),
    t.partial({
        authenticatorAttachment: AuthenticatorAttachment,
        createdDevice: t.string
    })
]);
const AuthenticatorAssertionResponseJSON = t.intersection([
    t.type({
        clientDataJSON: t.string,
        authenticatorData: t.string,
        signature: t.string
    }),
    t.partial({
        userHandle: t.string
    })
]);
/**
 * Custom fields out of fido2 standard, required to handle the authentication
 */
const CustomAuthenticationResponseJSON = t.partial({
    remember: t.boolean
});
/**
 * Authentication Request:
 * The {@link PublicKeyCredentialRequestOptions} that the server sends to the client to authenticate
 * see https://w3c.github.io/webauthn/#dictionary-assertion-options
 */
exports.PublicKeyCredentialRequestOptions = t.intersection([
    t.type({
        challenge: t.string,
        rpId: t.string,
        timeout: t.number,
        userVerification: UserVerificationRequirement
    }),
    t.partial({
        allowCredentials: t.array(PublicKeyCredentialDescriptor),
        attestation: t.string,
        attestationFormats: t.array(t.string),
        extensions: t.record(t.string, t.unknown)
    })
]);
/**
 * The authentication Verification follows the webauthn standard
 * see https://w3c.github.io/webauthn/#dictdef-authenticationresponsejson
 */
const BaseAuthenticationResponseJSON = t.intersection([
    t.type({
        id: t.string,
        rawId: t.string,
        response: AuthenticatorAssertionResponseJSON,
        clientExtensionResults: AuthenticationExtensionsClientOutputs,
        type: t.literal("public-key")
    }),
    t.partial({
        authenticatorAttachment: AuthenticatorAttachment
    })
]);
/**
 * Authentication Verification:
 * The response that the client sends to the server after signing in the previous received challenge
 * see https://w3c.github.io/webauthn/#dictdef-authenticationresponsejson
 */
exports.AuthenticationResponseJSON = t.intersection([
    BaseAuthenticationResponseJSON,
    CustomAuthenticationResponseJSON,
    t.type({
        email: t.string
    })
]);
/**
 * Authentication Verification:
 * The response that the client sends to the server after signing in the previous received challenge
 * In the V2 version of the API, the email is not required
 * see https://w3c.github.io/webauthn/#dictdef-authenticationresponsejson
 */
exports.AuthenticationResponseJSONV2 = t.intersection([
    BaseAuthenticationResponseJSON,
    CustomAuthenticationResponseJSON
]);
